import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import { ServiceRate, JOB_TYPE_OPTIONS, JobType } from '../InvoiceRegister/config';
import Modal from '../../components/Modal';
import Table from '../../components/Table';
import TableSkeleton from '../../components/TableSkeleton';
import StatusPill from '../../components/StatusPill';
import { PlusIcon } from '../../components/IconButton/icons';
import ListPage, { StatusSegments, useNarrowViewport } from '../../components/ListPage';
import { ColumnDef } from '@tanstack/react-table';
import { useUserRole } from '../../utils/useUserRole';
import './styles.scss';

interface RateFormData {
  job_type: string;
  rate_code: string;
  description: string;
  rate: string;
  is_active: boolean;
}

const emptyForm: RateFormData = {
  job_type: 'RWC',
  rate_code: '',
  description: '',
  rate: '',
  is_active: true,
};

const ServiceRates: React.FC = () => {
  const session = useSession();
  const supabase = useSupabaseClient();
  const { isReadOnly } = useUserRole();

  const [rates, setRates] = useState<ServiceRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<JobType>('RWC');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRate, setEditingRate] = useState<ServiceRate | null>(null);
  const [formData, setFormData] = useState<RateFormData>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const fetchRates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('service_rates')
        .select('*')
        .order('job_type')
        .order('rate_code');

      if (fetchError) throw fetchError;
      setRates(data || []);
    } catch (err) {
      console.error('Error fetching service rates:', err);
      setError('Failed to load service rates');
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    if (session?.user) {
      fetchRates();
    }
  }, [session, fetchRates]);

  const filteredRates = rates.filter(r => r.job_type === activeTab);

  const handleAdd = useCallback(() => {
    setEditingRate(null);
    setFormData({ ...emptyForm, job_type: activeTab });
    setFormError(null);
    setIsModalOpen(true);
  }, [activeTab]);

  const handleEdit = useCallback((rate: ServiceRate) => {
    setEditingRate(rate);
    setFormData({
      job_type: rate.job_type,
      rate_code: rate.rate_code,
      description: rate.description,
      rate: rate.rate.toString(),
      is_active: rate.is_active,
    });
    setFormError(null);
    setIsModalOpen(true);
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!formData.rate_code.trim()) {
      setFormError('Rate code is required');
      return;
    }
    if (!formData.description.trim()) {
      setFormError('Description is required');
      return;
    }
    const rateValue = parseFloat(formData.rate);
    if (isNaN(rateValue) || rateValue <= 0) {
      setFormError('Rate must be a positive number');
      return;
    }

    setIsSaving(true);
    try {
      if (editingRate) {
        const { error: updateError } = await supabase
          .from('service_rates')
          .update({
            job_type: formData.job_type,
            rate_code: formData.rate_code.trim(),
            description: formData.description.trim(),
            rate: rateValue,
            is_active: formData.is_active,
          })
          .eq('id', editingRate.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('service_rates')
          .insert({
            job_type: formData.job_type,
            rate_code: formData.rate_code.trim(),
            description: formData.description.trim(),
            rate: rateValue,
            is_active: formData.is_active,
          });

        if (insertError) throw insertError;
      }

      setIsModalOpen(false);
      await fetchRates();
    } catch (err) {
      console.error('Error saving service rate:', err);
      setFormError(err instanceof Error ? err.message : 'Failed to save rate');
    } finally {
      setIsSaving(false);
    }
  }, [formData, editingRate, supabase, fetchRates]);

  const handleToggleActive = useCallback(async (rate: ServiceRate) => {
    try {
      const { error: updateError } = await supabase
        .from('service_rates')
        .update({ is_active: !rate.is_active })
        .eq('id', rate.id);

      if (updateError) throw updateError;
      await fetchRates();
    } catch (err) {
      console.error('Error toggling rate:', err);
    }
  }, [supabase, fetchRates]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
    }).format(amount);

  const isNarrow = useNarrowViewport();
  const initialLoading = loading && rates.length === 0;

  const columns = useMemo<ColumnDef<ServiceRate>[]>(() => [
    {
      accessorKey: 'rate_code',
      header: 'Code',
      meta: { minWidth: '232px', width: '232px' }, // RWC_INSPECTION_REPORT and friends, in mono
      cell: ({ getValue }) => <span className="service-rates__code">{getValue<string>()}</span>,
    },
    {
      accessorKey: 'description',
      header: 'Description',
      meta: { minWidth: '240px', width: 'auto' },
      cell: ({ getValue }) => <span title={getValue<string>()}>{getValue<string>()}</span>,
    },
    {
      accessorKey: 'rate',
      header: 'Rate (ex GST)',
      meta: { minWidth: '140px', width: '140px', align: 'right' },
      cell: ({ getValue }) => formatCurrency(getValue<number>()),
    },
    {
      accessorKey: 'is_active',
      header: 'Status',
      meta: { minWidth: '112px', width: '112px' },
      cell: ({ getValue }) =>
        getValue<boolean>() ? <StatusPill label="Active" tone="success" /> : <StatusPill label="Inactive" tone="neutral" />,
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      meta: { minWidth: '212px', width: '212px', align: 'right' },
      cell: ({ row }) => (
        <span className="service-rates__actions" onClick={(e) => e.stopPropagation()}>
          <button type="button" className="pill-btn pill-btn--sm" onClick={() => handleEdit(row.original)} disabled={isReadOnly}>
            Edit
          </button>
          <button type="button" className="pill-btn pill-btn--ghost pill-btn--sm" onClick={() => handleToggleActive(row.original)} disabled={isReadOnly}>
            {row.original.is_active ? 'Deactivate' : 'Activate'}
          </button>
        </span>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [isReadOnly]);

  const modalFooter = (
    <div className="modal-footer-actions">
      <button type="button" onClick={() => setIsModalOpen(false)} className="btn-secondary">
        Cancel
      </button>
      <button
        type="submit"
        form="service-rate-form"
        disabled={isSaving}
        className="btn-primary"
      >
        {isSaving ? 'Saving...' : editingRate ? 'Update Rate' : 'Add Rate'}
      </button>
    </div>
  );

  return (
    <ListPage
      title="Service Rates"
      className="service-rates"
      loading={initialLoading}
      skeleton={{ segmentCount: 3, filterCount: 0, showAction: true }}
      actions={
        <button type="button" className="pill-btn pill-btn--solid pill-btn--sm" onClick={handleAdd} disabled={isReadOnly}>
          <PlusIcon /> Add rate
        </button>
      }
      segments={
        <StatusSegments<JobType>
          aria-label="Job type"
          size={isNarrow ? 'sm' : 'md'}
          value={activeTab}
          onChange={setActiveTab}
          segments={JOB_TYPE_OPTIONS.map((type) => ({
            value: type,
            label: type,
            count: rates.filter((r) => r.job_type === type).length,
          }))}
        />
      }
      resultsText={
        !initialLoading && `${filteredRates.length} ${filteredRates.length === 1 ? 'rate' : 'rates'} for ${activeTab} projects`
      }
    >
      {error && (
        <div className="list-page__error">
          {error}{' '}
          <button type="button" className="link-btn" onClick={fetchRates}>Try again</button>
        </div>
      )}

      {initialLoading ? (
        <TableSkeleton compact rows={10} columns={5} showHeader />
      ) : filteredRates.length === 0 ? (
        <div className="list-page__empty">No service rates configured for {activeTab} projects.</div>
      ) : (
        <Table data={filteredRates} columns={columns} stickyHeader />
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingRate ? 'Edit Service Rate' : 'Add Service Rate'}
        size="sm"
        hasFooter={true}
        footerContent={modalFooter}
      >
        <form id="service-rate-form" onSubmit={handleSubmit} className="service-rate-form">
          {formError && <div className="service-rate-form__error">{formError}</div>}

          <div className="service-rate-form__group">
            <label htmlFor="sr-job-type">Job Type</label>
            <select
              id="sr-job-type"
              value={formData.job_type}
              onChange={(e) => setFormData(prev => ({ ...prev, job_type: e.target.value }))}
            >
              {JOB_TYPE_OPTIONS.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div className="service-rate-form__group">
            <label htmlFor="sr-rate-code">Rate Code *</label>
            <input
              id="sr-rate-code"
              type="text"
              value={formData.rate_code}
              onChange={(e) => setFormData(prev => ({ ...prev, rate_code: e.target.value }))}
              placeholder="e.g. RWC_STARTUP"
            />
          </div>

          <div className="service-rate-form__group">
            <label htmlFor="sr-description">Description *</label>
            <input
              id="sr-description"
              type="text"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="e.g. Startup Meeting"
            />
          </div>

          <div className="service-rate-form__group">
            <label htmlFor="sr-rate">Rate (ex GST) *</label>
            <div className="service-rate-form__currency-input">
              <span className="service-rate-form__currency-symbol">$</span>
              <input
                id="sr-rate"
                type="number"
                step="0.01"
                min="0.01"
                value={formData.rate}
                onChange={(e) => setFormData(prev => ({ ...prev, rate: e.target.value }))}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="service-rate-form__group service-rate-form__group--checkbox">
            <label>
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
              />
              Active
            </label>
          </div>
        </form>
      </Modal>
    </ListPage>
  );
};

export default ServiceRates;
